import { useState, useEffect, useCallback, useMemo } from 'react'
import { metricsApi } from '../../api/metrics'
import type { MetricSnapshot, MetricSummary } from '../../types'

export type TimePeriod = '1h' | '6h' | '24h' | '7d'

export interface MetricsData {
  timeSeries: MetricSnapshot[]
  summaries: Record<string, MetricSummary>
  availableMetrics: string[]
  loading: boolean
  error: string | null
  refetch: () => void
}

// Key metrics to fetch summaries for
const KEY_METRICS = [
  'http_requests_total',
  'http_request_duration_p50',
  'http_request_duration_p95',
  'http_request_duration_p99',
  'http_errors_total',
  'cpu_usage_percent',
  'memory_usage_bytes',
  'memory_usage_percent',
  'disk_usage_percent',
  'active_connections',
]

function getHoursForPeriod(period: TimePeriod): number {
  switch (period) {
    case '1h':
      return 1
    case '6h':
      return 6
    case '24h':
      return 24
    case '7d':
      return 168
  }
}

export function useMetricsData(appId: string, period: TimePeriod = '24h'): MetricsData {
  const [timeSeries, setTimeSeries] = useState<MetricSnapshot[]>([])
  const [summaries, setSummaries] = useState<Record<string, MetricSummary>>({})
  const [availableMetrics, setAvailableMetrics] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const hours = getHoursForPeriod(period)

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      // Fetch available metrics first to know what summaries to request
      const available = await metricsApi.getAvailable(appId)
      setAvailableMetrics(available.metrics || [])

      // Fetch time series data
      const timeSeriesResponse = await metricsApi.list(appId, {
        hours,
        limit: period === '7d' ? 200 : 100,
      })
      setTimeSeries(timeSeriesResponse.snapshots || [])

      // Fetch summaries for key metrics that are available
      const metricsToFetch = KEY_METRICS.filter(m => available.metrics?.includes(m))
      const summaryPromises = metricsToFetch.map(async metric => {
        try {
          const summary = await metricsApi.getSummary(appId, metric, hours)
          return { metric, summary }
        } catch {
          return null
        }
      })

      const summaryResults = await Promise.all(summaryPromises)
      const summaryMap: Record<string, MetricSummary> = {}
      summaryResults.forEach(result => {
        if (result) {
          summaryMap[result.metric] = result.summary
        }
      })
      setSummaries(summaryMap)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch metrics')
    } finally {
      setLoading(false)
    }
  }, [appId, hours, period])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  return useMemo(
    () => ({
      timeSeries,
      summaries,
      availableMetrics,
      loading,
      error,
      refetch: fetchData,
    }),
    [timeSeries, summaries, availableMetrics, loading, error, fetchData]
  )
}
